import type React from 'react';
import {
  createContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import {
  areAdditionalPolicyHoldersEqual,
  getCookie,
  getQueryString,
  getValueFromId,
  isGUID,
  setCookie,
  useSyncContextToSessionStorage,
} from '@agria/utils';
import type { PageProps } from 'gatsby';
import {
  AuthorityToDeal,
  Basket,
  Customer,
  Pet,
  Quote,
  EquineQuote,
  AffiliateBreederClubEnrolmentRequest,
  PetEquine,
  Benefit,
  LloydsMarketingConsents,
} from '@agria/paws/src/types';
import { useGetBasket } from '@agria/paws/src/hooks/useGetBasket';
import { PawsBrand, useBrand } from '@agria/paws/src/hooks/useBrand';
import { useCampaignId } from '@agria/paws/src/hooks/useCampaignId';
import { useAffiliateLookup } from '@agria/paws/src/hooks/useAffiliateLookup';
import * as Sentry from '@sentry/gatsby';
import { useSiteMetadata } from '@agria/theme/src/hooks/useSiteMetadata';
import useGetDataLayerContentType from '../hooks/useGetDataLayerContentType';
import { subSpeciesIds } from '../const/subSpeciesIds';
import { useDefaultPromoCode } from '../hooks/useDefaultPromoCode';
import { speciesReference } from '../const/speciesReference';

export interface BreederPolicyCustomer extends Partial<Customer> {
  AdditionalTitleId?: string;
  AdditionalFirstName?: string;
  AdditionalLastName?: string;
}
export interface BreederPolicyRequest {
  Pet: Partial<Pet>;
  Customer?: BreederPolicyCustomer;
  CustomerId?: number;
  id: number;
}
interface QuoteContextProviderProps {
  children: React.ReactNode;
  location: PageProps['location'];
}

interface CampaignContextState {
  campaignId: string | null;
  affiliateId: string | null;
  partnerTrackingId: string | null;
  sskey: string | null;
  utmSource: string | null;
}

export interface RequestedQuoteItem {
  quoteIds?: string[]; // array of quote IDs
  count: number;
}

interface ProductOptionsMap {
  [quoteId: string]: Benefit;
}

export interface AffiliateResponse {
  AgriaId: string;
  EmailAddress: string;
  PostCode: string;
}

export interface DirectDebitAmount {
  policyId: string;
  amount: number;
}
// interface QuoteProgress {
//   currentStep: string; // TODO Type This! or do we need it now with Active Step Context?
//   requestedQuotes: Record<string, RequestedQuote>;
// }
// interface ActiveSpecies {
//   name: AllowedSpecies;
//   id: string;
// }

export type RequestedQuotes = Record<string, RequestedQuoteItem>;

interface QuoteContextState {
  activeQuote: Quote | EquineQuote | null; // Currently Active Quote, may not be in basket yet
  activeSpecies?: string | null; // Currently Active species to quote on
  additionalPolicyHolder: Partial<AuthorityToDeal> | null;
  // Aggregator parameters
  Urn?: string | null;
  Ssid?: string | null;
  CoverType?: string | null;
  VetFeeParameters?: string | null;
  affiliateEnrolment:
    | Partial<AffiliateBreederClubEnrolmentRequest>
    | Record<string, any>
    | null;
  affiliatePolicyRequests?: Partial<BreederPolicyRequest>[];
  affiliateResponse?: AffiliateResponse | null;
  basket: Basket | null; // Complete basket returned from GetBasket
  basketError?: any;
  basketId: string | null; //  basket ID returned from GetBasket
  basketLoading?: boolean;
  brand: PawsBrand | null; // Active Brand e.g Agria / KC
  campaign: CampaignContextState; // Marketing information
  contextProductOptions?: Benefit | null;
  dataLayerContentType?: string;
  directDebitAmounts: DirectDebitAmount[];
  editMode?: string | number | null; // quote id (can be number on affliate)
  extraData?: Record<string, any>;
  fetchBasket: () => void;
  hadPriorThirdPartyLiabilityClaim?: boolean; // True if had a prior third party liability claim (not eligible for TPL coverage)
  aggressiveBehaviour?: boolean; // Looks at the current products and checks if there is a third party liability feature
  hasSet?: boolean;
  isContinueCover: boolean;
  petInfos: Record<string, any>;
  policyIds?: string[];
  promoCode?: string;
  quoteType: 'continue cover' | 'retrieve quote' | 'new quote';
  dataLayerProductOptionsMap: ProductOptionsMap;
  dataLayerProductOptions?: Benefit | null;
  requestedQuotes: RequestedQuotes | null; // Tracks progress through journey
  userSetExcessIds: Record<string, boolean>;
  lloydsMarketingConsents: Partial<LloydsMarketingConsents>;
  chronologicalQuoteIds: string[];
  SelectedProductType?: string; // Selected product type for filtering products
}

interface QuoteContextStateReturn extends QuoteContextState {
  dispatch: React.Dispatch<Action>;
  totalQuoteRequests: number;
  totalCompletedQuoteRequests: number;
  allActiveSpeciesCount: number;
  refetchBasket: () => void;
  hasBasket: boolean;
  activeSpeciesCount: number;
  activeSpeciesTotalCount: number;
  promoCode?: string;
  userSetExcessIds: Record<string, boolean>;
  directDebitAmounts: DirectDebitAmount[];
}
export interface QueryContextQueryStrings {
  affid?: string;
  basketId?: string;
  camid?: string;
  cover_type?: string;
  partnertrackingid?: string;
  pc?: string;
  Postcode?: string;
  promocode?: string;
  QuoteRef?: string;
  quoterefGuid?: string;
  ssid?: string;
  sskey?: string;
  URN?: string;
  utm_source?: string;
  vet_fee?: string;
}

const initialCampaignState: CampaignContextState = {
  campaignId: null,
  affiliateId: null,
  partnerTrackingId: null,
  sskey: null,
  utmSource: null,
};

const initialState: QuoteContextState = {
  campaign: initialCampaignState,
  activeQuote: null,
  activeSpecies: null,
  basket: null,
  basketId: null,
  basketError: null,
  requestedQuotes: null,
  brand: null,
  additionalPolicyHolder: null,
  // Aggregator parameters
  Urn: null,
  Ssid: null,
  CoverType: null,
  VetFeeParameters: null,
  fetchBasket: () => console.warn('fetchBasket not implemented'),
  policyIds: [],
  isContinueCover: false,
  affiliatePolicyRequests: [],
  affiliateEnrolment: null,
  affiliateResponse: null,
  basketLoading: false,
  contextProductOptions: null,
  promoCode: '',
  petInfos: {},
  userSetExcessIds: {},
  quoteType: 'new quote',
  directDebitAmounts: [],
  dataLayerProductOptionsMap: {},
  dataLayerProductOptions: null,
  chronologicalQuoteIds: [],
  lloydsMarketingConsents: {
    LloydsOptOutAppNotifications: undefined,
    LloydsOptOutEmail: undefined,
    LloydsOptOutPhone: undefined,
    LloydsOptOutPost: undefined,
    LloydsOptOutSms: undefined,
  },
};

export type Action =
  | {
      type: 'SET_ALL';
      payload: any | null;
    }
  | {
      type: 'COMPLETE_PURCHASE';
      payload?: null;
    }
  | {
      type: 'RESET';
      payload?: null;
    }
  | {
      type: 'SET_CONTINUE_COVER';
      payload: boolean;
    }
  | {
      type: 'SET_EXTRA_DATA';
      payload: Record<string, any>;
    }
  | {
      type: 'SET_PROMO_CODE';
      payload: string;
    }
  | {
      type: 'REMOVE_PROMO_CODE';
      payload?: null;
    }
  | {
      type: 'SET_TERMS_AGREED';
      payload: boolean;
    }
  | {
      type: 'SET_HAS_SET';
      payload: boolean;
    }
  | {
      type: 'SET_CAMPAIGN_ID';
      payload: string | null;
    }
  | {
      type: 'SET_PARTNER_ID';
      payload: string | null;
    }
  | {
      type: 'SET_BASKET_ID';
      payload: string | null;
    }
  | {
      type: 'SET_AFFILIATE_ID';
      payload: string | null;
    }
  | {
      type: 'SET_AFFILIATE_ENROLMENT';
      payload: Partial<AffiliateBreederClubEnrolmentRequest> | null;
    }
  | {
      type: 'SET_AFFILIATE_RESPONSE';
      payload: AffiliateResponse | Record<string, any> | null;
    }
  | {
      type: 'SET_BRAND';
      payload: PawsBrand;
    }
  | {
      type: 'SET_SSKEY';
      payload: string | null;
    }
  | {
      type: 'SET_UTM_SOURCE';
      payload: string | null;
    }
  | {
      type: 'SET_QUOTE_REQUESTS';
      payload: RequestedQuotes | null;
    }
  | {
      type: 'CANCEL_OUTSTANDING_QUOTE_REQUESTS';
    }
  | {
      type: 'SET_ACTIVE_PET';
      payload: Partial<Pet | PetEquine> | null;
    }
  | {
      type: 'SET_ACTIVE_CUSTOMER';
      payload: Partial<Customer> | null;
    }
  | {
      type: 'SET_ACTIVE_QUOTE';
      payload: Partial<Quote | EquineQuote> | null;
    }
  | {
      type: 'SET_ACTIVE_QUOTE_BY_ID';
      payload: string;
    }
  | {
      type: 'SET_BASKET';
      payload: Basket | null;
    }
  | {
      type: 'SET_POLICES';
      payload: string[];
    }
  | {
      type: 'SET_ADDITIONAL_POLICY_HOLDER';
      payload: Partial<AuthorityToDeal> | null;
    }
  | {
      type: 'REMOVE_ADDITIONAL_POLICY_HOLDER';
    }
  | {
      type: 'SET_ALLOW_FOLLOW_UP';
      payload: boolean;
    }
  | {
      type: 'SET_DATALAYER_CONTENT_TYPE';
      payload: string;
    }
  | {
      type: 'SET_CONTEXT_PRODUCT_OPTIONS';
      payload: Benefit;
    }
  | {
      type: 'SET_DATALAYER_PRODUCT_OPTIONS';
      payload: {
        quoteId: string;
        options: Benefit;
      };
    }
  | {
      type: 'SET_EDIT_MODE';
      payload: string | number | null;
    }
  | {
      type: 'SET_HAD_THIRDPARTY_LIABILITY_CLAIM';
      payload: boolean;
    }
  | {
      type: 'SET_AGGRESSIVE_BEHAVIOUR';
      payload: boolean;
    }
  | {
      type: 'SET_BREEDER_POLICY_REQUEST';
      payload: Partial<BreederPolicyRequest>;
    }
  | {
      type: 'SET_BREEDER_POLICY_OWNER';
      payload: {
        customer: Partial<Customer>;
        id: number;
      };
    }
  | {
      type: 'SET_EXCESS';
      payload: {
        selectedExcessId?: string;
        value?: string;
        selectedFixedExcessId?: string;
        fixedExcessValue?: string;
        selectedVariableExcessId?: string;
        variableExcessValue?: string;
        quoteId: string;
      };
    }
  | {
      type: 'REMOVE_EXCESS';
      payload: {
        quoteId: string | undefined;
      };
    }
  | {
      type: 'SET_BREEDER_POLICY_PREVIOUS_OWNER';
      payload: {
        customerId: number;
        id: number;
      };
    }
  | {
      type: 'SET_PET_INFO';
      payload: { key: string; info: any };
    }
  | {
      type: 'REMOVE_PET_INFOS_WITHOUT_POLICY_VALUE';
    }
  | {
      type: 'SET_QUOTE_TYPE';
      payload: 'continue cover' | 'retrieve quote' | 'new quote';
    }
  | { type: 'SET_DIRECT_DEBIT_AMOUNT'; payload: DirectDebitAmount }
  | {
      type: 'SET_LLOYDS_MARKETING_CONSENTS';
      payload: Partial<Exclude<LloydsMarketingConsents, 'CustomerId'>>;
    }
  | {
      type: 'INITIALISE_PET_INFOS';
      payload: RequestedQuotes;
    }
  | {
      type: 'UPDATE_ALL_PET_INFOS';
      payload: Record<string, any>;
    }
  | {
      type: 'SET_SELECTED_PRODUCT_TYPE';
      payload: string;
    }
  | {
      type: 'SET_AGGREGATOR_PARAMS';
      payload: {
        CoverType?: string | null;
        Ssid?: string | null;
        Urn?: string | null;
        VetFeeParameters?: string | null;
      };
    };

const reducer = (state: QuoteContextState, action: Action) => {
  console.debug('🔍 Action.type:', action.type);

  switch (action.type) {
    case 'SET_ALL':
      return {
        ...action.payload,
        // Preserve aggregator params if they exist in current state
        ...(state.Urn && { Urn: state.Urn }),
        ...(state.Ssid && { Ssid: state.Ssid }),
        ...(state.CoverType && { CoverType: state.CoverType }),
        ...(state.VetFeeParameters && {
          VetFeeParameters: state.VetFeeParameters,
        }),
      };
    case 'COMPLETE_PURCHASE': {
      const prev = { ...state };
      prev.requestedQuotes = null;
      prev.additionalPolicyHolder = null;
      prev.editMode = null;
      // prev.activeQuote = null;
      return {
        ...prev,
      };
    }
    case 'RESET': {
      const prev = { ...state };
      prev.requestedQuotes = null;
      prev.additionalPolicyHolder = null;
      prev.activeSpecies = null;
      prev.activeQuote = null;
      prev.extraData = undefined;
      prev.basketId = null;
      prev.basket = null;
      prev.isContinueCover = false;
      prev.editMode = null;
      return {
        ...prev,
      };
    }
    case 'SET_CONTINUE_COVER':
      return {
        ...state,
        isContinueCover: action.payload,
      };
    case 'SET_BREEDER_POLICY_REQUEST': {
      // if id in payload.id already exists in the affiliatePolicyRequests array, replace it else add it
      const affiliatePolicyRequests = [
        ...(state.affiliatePolicyRequests ?? []),
      ];
      const index = affiliatePolicyRequests.findIndex(
        (item) => item.id === action.payload.id,
      );

      if (index !== -1) {
        affiliatePolicyRequests[index] = action.payload;
      } else {
        affiliatePolicyRequests.push(action.payload);

        // get the Pet.SpeciesId and then add the id to the requestedQuotes quoteIds array
        const speciesId = action.payload.Pet?.SpeciesId;
        if (speciesId) {
          const { requestedQuotes } = state;
          if (requestedQuotes && action.payload.id) {
            requestedQuotes[speciesId]?.quoteIds?.push(`${action.payload.id}`);
          }
        }
      }

      return {
        ...state,
        affiliatePolicyRequests,
      };
    }
    case 'SET_AFFILIATE_RESPONSE':
      return {
        ...state,
        affiliateResponse: action.payload || null,
      };
    case 'SET_CONTEXT_PRODUCT_OPTIONS':
      return {
        ...state,
        contextProductOptions: action.payload || null,
      };
    case 'SET_DATALAYER_PRODUCT_OPTIONS':
      return {
        ...state,
        // Store options in the map
        dataLayerProductOptionsMap: {
          ...state.dataLayerProductOptionsMap,
          [action.payload.quoteId]: action.payload.options,
        },
        // Keep the current quote's options in the legacy field
        dataLayerProductOptions:
          state.activeQuote?.Id === action.payload.quoteId
            ? action.payload.options
            : state.dataLayerProductOptions,
      };
    case 'SET_HAD_THIRDPARTY_LIABILITY_CLAIM':
      return {
        ...state,
        hadPriorThirdPartyLiabilityClaim: action.payload || false,
        activeQuote: state.activeQuote
          ? {
              ...state.activeQuote,
              Pet: state.activeQuote.Pet
                ? {
                    ...state.activeQuote.Pet,
                    HadPriorThirdPartyLiabilityClaim: action.payload || false,
                  }
                : state.activeQuote.Pet,
            }
          : state.activeQuote,
      };
    case 'SET_AGGRESSIVE_BEHAVIOUR':
      return {
        ...state,
        aggressiveBehaviour: action.payload || false,
        activeQuote: state.activeQuote
          ? {
              ...state.activeQuote,
              Pet: state.activeQuote.Pet
                ? {
                    ...state.activeQuote.Pet,
                    AggressiveBehaviour: action.payload || false,
                  }
                : state.activeQuote.Pet,
            }
          : state.activeQuote,
      };
    case 'SET_BREEDER_POLICY_OWNER': {
      const affiliatePolicyRequests = [
        ...(state.affiliatePolicyRequests ?? []),
      ];
      const index = affiliatePolicyRequests.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        affiliatePolicyRequests[index].Customer = action.payload.customer;
        delete affiliatePolicyRequests[index].CustomerId;
      }
      return {
        ...state,
        affiliatePolicyRequests,
      };
    }
    case 'SET_BREEDER_POLICY_PREVIOUS_OWNER': {
      const affiliatePolicyRequests = [
        ...(state.affiliatePolicyRequests ?? []),
      ];
      const index = affiliatePolicyRequests.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        affiliatePolicyRequests[index].CustomerId = action.payload.customerId;
        delete affiliatePolicyRequests[index].Customer;
      }
      return {
        ...state,
        affiliatePolicyRequests,
      };
    }
    case 'SET_PROMO_CODE':
      return {
        ...state,
        promoCode: action.payload,
        activeQuote: {
          ...state.activeQuote,
          PromoCode: action.payload,
        },
      };
    case 'REMOVE_PROMO_CODE':
      return {
        ...state,
        promoCode: '',
        activeQuote: {
          ...state.activeQuote,
          PromoCode: '',
        },
      };
    case 'SET_TERMS_AGREED':
      return {
        ...state,
        activeQuote: {
          ...state.activeQuote,
          TermsAgreed: action.payload,
        },
      };

    case 'SET_HAS_SET':
      return {
        ...state,
        hasSet: action.payload,
      };

    case 'SET_AFFILIATE_ENROLMENT':
      return {
        ...state,
        affiliateEnrolment: action.payload,
      };
    case 'SET_BASKET_ID':
      return {
        ...state,
        basketId: action.payload ?? null,
      };
    case 'SET_CAMPAIGN_ID':
      return {
        ...state,

        activeQuote:
          state.activeQuote && action.payload
            ? {
                ...state.activeQuote,
                CampaignId: action.payload ?? null,
              }
            : null,
        campaign: {
          ...state.campaign,
          campaignId: action.payload ?? null,
        },
      };
    case 'SET_PARTNER_ID':
      return {
        ...state,

        activeQuote:
          state.activeQuote && action.payload
            ? {
                ...state.activeQuote,
                PartnerTrackingId: action.payload ?? null,
              }
            : null,
        campaign: {
          ...state.campaign,
          partnerTrackingId: action.payload ?? null,
        },
      };
    case 'SET_AFFILIATE_ID':
      return {
        ...state,
        activeQuote:
          state.activeQuote && action.payload
            ? {
                ...state.activeQuote,
                AffiliateId: action.payload ?? null,
              }
            : null,
        campaign: {
          ...state.campaign,
          affiliateId: action.payload ?? null,
        },
      };
    case 'SET_SSKEY':
      return {
        ...state,
        campaign: {
          ...state.campaign,
          sskey: action.payload ?? null,
        },
      };
    case 'SET_UTM_SOURCE':
      return {
        ...state,
        campaign: {
          ...state.campaign,
          utmSource: action.payload ?? null,
        },
      };
    case 'SET_DATALAYER_CONTENT_TYPE':
      return {
        ...state,
        dataLayerContentType: action.payload,
      };
    case 'SET_POLICES':
      return {
        ...state,
        policyIds: [...action.payload],
      };
    case 'SET_EXTRA_DATA':
      return {
        ...state,
        extraData: action.payload,
      };
    case 'SET_BRAND': {
      try {
        Sentry.setTag('agria_brand', action.payload.name);
      } catch (error) {
        // do nothing
      }
      return {
        ...state,
        brand: action.payload,

        activeQuote: {
          ...(state.activeQuote ?? {}),
          BrandId: action.payload.brandId,
        },
      };
    }
    case 'SET_LLOYDS_MARKETING_CONSENTS':
      return {
        ...state,
        lloydsMarketingConsents: action.payload || {
          LloydsOptOutAppNotifications: undefined,
          LloydsOptOutEmail: undefined,
          LloydsOptOutPhone: undefined,
          LloydsOptOutPost: undefined,
          LloydsOptOutSms: undefined,
        },
      };
    case 'SET_SELECTED_PRODUCT_TYPE':
      return {
        ...state,
        SelectedProductType: action.payload,
      };
    case 'SET_AGGREGATOR_PARAMS':
      return {
        ...state,
        CoverType: action.payload.CoverType ?? state.CoverType,
        Ssid: action.payload.Ssid ?? state.Ssid,
        Urn: action.payload.Urn ?? state.Urn,
        VetFeeParameters:
          action.payload.VetFeeParameters ?? state.VetFeeParameters,
      };
    case 'CANCEL_OUTSTANDING_QUOTE_REQUESTS': {
      // remove and property without quoteIds and if quoteIds is less the count set the count to the length of quoteIds
      const requestedQuotes = { ...state.requestedQuotes };
      Object.entries(requestedQuotes).forEach(([key, value]) => {
        if (value.count > 0 && value.count !== value?.quoteIds?.length) {
          value.count = value?.quoteIds?.length ?? 0;
        } else if (value.count === 0) {
          delete requestedQuotes[key];
        }
      });
      return {
        ...state,
      };
    }

    case 'INITIALISE_PET_INFOS': {
      const { payload } = action;
      const totalPets = Object.values(payload).reduce(
        (sum, quote) => sum + quote.count,
        0,
      );

      // Create initial pet_info objects for all requested pets, preserving existing data
      const initialPetInfos: Record<string, any> = {};
      const existingPetInfos = state.petInfos || {};

      for (let i = 1; i <= totalPets; i += 1) {
        const petInfoKey = `pet_info_${i}`;
        const existingPetInfo = existingPetInfos[petInfoKey] || {};

        // Only set basic species info if this pet doesn't already have detailed information
        const shouldPreserveExisting =
          existingPetInfo.cover_type ||
          existingPetInfo.policy_value ||
          existingPetInfo.excesses_fixed ||
          Object.keys(existingPetInfo).length > 1;

        if (shouldPreserveExisting) {
          // Preserve all existing data for pets that have detailed information
          initialPetInfos[petInfoKey] = existingPetInfo;
        } else {
          // Only initialize with basic species info for new pets
          const speciesEntry = Object.entries(payload).find(
            ([, quote]) => quote.count >= i,
          );
          const speciesId = speciesEntry?.[0];

          initialPetInfos[petInfoKey] = {
            // Initialize with basic species information from the requestedQuotes
            pet_species: speciesId
              ? speciesReference[speciesId]?.name
              : undefined,
          };
        }
      }

      return {
        ...state,
        petInfos: {
          ...existingPetInfos,
          ...initialPetInfos,
        },
      };
    }

    case 'UPDATE_ALL_PET_INFOS': {
      return {
        ...state,
        petInfos: {
          ...state.petInfos,
          ...action.payload,
        },
      };
    }

    case 'SET_QUOTE_REQUESTS': {
      const activeSpecies = action.payload
        ? Object.keys(action.payload)[0]
        : null;

      // Check if another pet in requestedQuotes is of the same species (objects key)
      const nextQuoteSpecies = action.payload
        ? Object.entries(action.payload).find(
            ([_, value]) =>
              value.count >= 1 &&
              value.count !== (value?.quoteIds?.length ?? 0),
          )
        : undefined;

      // if new quote request does  contain any quoteIds then we need to keep the basket if not remove it

      let BasketId = state.basketId ?? state.activeQuote?.BasketId;
      let { basket, basketId } = state;
      let { chronologicalQuoteIds } = state;

      if (
        action.payload &&
        !Object.values(action.payload).some(
          (item) => item?.quoteIds && item?.quoteIds?.length > 0,
        )
      ) {
        basket = null;
        BasketId = undefined;
        basketId = null;
        chronologicalQuoteIds = [];
      }

      // Initialize pet_info objects for all selected pets
      const updatedPetInfos = { ...state.petInfos };
      if (action.payload) {
        let currentPetNumber = 1;
        Object.entries(action.payload).forEach(([speciesId, quoteDetails]) => {
          for (let i = 0; i < quoteDetails.count; i += 1) {
            const petInfoKey = `pet_info_${currentPetNumber}`;
            const existingSpecies = updatedPetInfos[petInfoKey]?.pet_species;
            const newSpecies = speciesReference[speciesId]?.name?.toLowerCase();

            updatedPetInfos[petInfoKey] = {
              ...updatedPetInfos[petInfoKey], // Preserve existing data if any
              // Only set pet_species if it doesn't already exist to preserve existing species
              pet_species: existingSpecies || newSpecies,
              // We're only initializing/updating the species here, other data will be added later
              // in the journey
            };

            currentPetNumber += 1;
          }
        });
      }

      return {
        ...state,
        basketId,
        basket,
        requestedQuotes: action.payload ?? null,
        chronologicalQuoteIds,
        affiliatePolicyRequests: [],
        editMode: null,
        activeQuote: {
          BrandId: state.brand?.brandId ?? null,
          Customer: state.activeQuote?.Customer ?? null,
          BasketId,
          Pet: {
            SpeciesId: nextQuoteSpecies?.[0] ?? activeSpecies,
          },
        },
        petInfos: updatedPetInfos,
      };
    }
    case 'SET_ACTIVE_QUOTE': {
      // if this Quote.Id is not in the requestedQuotes then we need to add it

      const speciesId = action.payload?.Pet?.SpeciesId;
      const currentRequestedQuotes = state?.requestedQuotes ?? {};

      const currentRequest = currentRequestedQuotes?.[speciesId!];
      const currentQuoteIds = speciesId
        ? currentRequest?.quoteIds?.filter(
            (value, index, self) => self.indexOf(value) === index,
          )
        : [];

      const updatedChronologicalQuoteIds = [...state.chronologicalQuoteIds];
      if (
        action.payload?.Id &&
        !updatedChronologicalQuoteIds.includes(action.payload.Id)
      ) {
        updatedChronologicalQuoteIds.push(action.payload.Id);
      }

      // Step 1: Create a new array with existing quote IDs and the new one from the action payload.
      // currentQuoteIds || [] ensures that if currentQuoteIds is undefined or null, an empty array is used instead.
      // const newQuoteIds = [
      //   ...(currentQuoteIds || []), // Spread the currentQuoteIds (if any) into a new array
      //   action?.payload?.Id, // Add the new ID from action.payload, safely accessing it (optional chaining)
      // ]
      //   // Step 2: Filter out any falsy values (null, undefined, 0, etc.) from the array.
      //   .filter(Boolean) // Keeps only truthy values (removes undefined, null, etc.)
      //   // Step 3: Remove duplicates by keeping only the first occurrence of each value.
      //   // 'self.indexOf(value)' gives the first occurrence of 'value', so we keep it only if the current index matches.
      //   .filter((value, index, self) => self.indexOf(value) === index); // Removes duplicates by comparing indices
      // const requestedQuotes =
      //   action?.payload?.Id && speciesId
      //     ? {
      //         ...state.requestedQuotes,
      //         [speciesId]: {
      //           quoteIds: newQuoteIds,
      //           count: currentQuoteIds?.length ?? 1,
      //         },
      //       }
      //     : state.requestedQuotes;

      // Only update quoteIds if we have a new quote ID
      let { requestedQuotes } = state;
      if (action?.payload?.Id && speciesId) {
        const newQuoteIds = [...(currentQuoteIds || [])];

        // Only add the new ID if it doesn't already exist
        if (!newQuoteIds.includes(action.payload.Id)) {
          newQuoteIds.push(action.payload.Id);
        }

        requestedQuotes = {
          ...state.requestedQuotes,
          [speciesId]: {
            quoteIds: newQuoteIds,
            // Preserve the original count
            count: currentRequest?.count ?? newQuoteIds.length,
          },
        };
      }

      try {
        Sentry.setContext('activeQuote', {
          SpeciesId: speciesId,
          BreedId: action.payload?.Pet?.BreedId,
          Id: action.payload?.Id,
          AffiliateId: action?.payload?.AffiliateId,
          PartnerTrackingId: action?.payload?.PartnerTrackingId,
          BasketId: action?.payload?.BasketId,
          BrandId: action?.payload?.BrandId,
          CampaignId: action?.payload?.CampaignId,
          ProductId: action?.payload?.ProductId,
          QuoteReference: action?.payload?.QuoteReference,
          TotalBasketPrice: action?.payload?.TotalBasketPrice,
          SelectedFixedExcessId: action?.payload?.SelectedFixedExcessId,
          CustomerId: action?.payload?.Customer?.Id,
          PetId: action?.payload?.Pet?.Id,
          PetName: action?.payload?.Pet?.Name,
          IsRenewalQuote: action?.payload?.IsRenewalQuote,
        });

        if (action.payload?.Id) {
          Sentry.setTag('quote_id', action.payload.Id);
        }
        if (speciesId) {
          Sentry.setTag('species_id', speciesId);
        }

        if (action?.payload?.Customer?.Id) {
          Sentry.setUser({
            id: action.payload?.Customer.Id,
          });
        }
      } finally {
        // NOOP
      }

      const newActiveQuote = {
        ...action.payload,
        UserSetExcessId: state.userSetExcessIds[action.payload.Id] ?? false,
      };

      // Preserve excess values from state when payload doesn't provide them
      if (state.userSetExcessIds[newActiveQuote.Id]) {
        // Only use state value if payload value is undefined/null
        if (
          !action.payload?.SelectedFixedExcessId &&
          state.activeQuote?.SelectedFixedExcessId
        ) {
          newActiveQuote.SelectedFixedExcessId =
            state.activeQuote.SelectedFixedExcessId;
        }
        if (
          !action.payload?.SelectedVariableExcessId &&
          state.activeQuote?.SelectedVariableExcessId
        ) {
          newActiveQuote.SelectedVariableExcessId =
            state.activeQuote.SelectedVariableExcessId;
        }
      }

      if (state.activeQuote?.AllowFollowUp !== undefined) {
        newActiveQuote.AllowFollowUp = state.activeQuote.AllowFollowUp;
      }

      const updatedBasketQuotes =
        state.basket?.Quotes.map((quote) =>
          quote.Id === newActiveQuote.Id
            ? { ...quote, ...newActiveQuote }
            : quote,
        ) ?? [];

      const returnState = {
        ...state,
        activeQuote: newActiveQuote,
        requestedQuotes,
        chronologicalQuoteIds: updatedChronologicalQuoteIds,
        basket: state.basket
          ? {
              ...state.basket,
              Quotes: updatedBasketQuotes,
            }
          : null,
      };

      // If we have authority to deal data in the API response, update additionalPolicyHolder
      if (
        'AuthorityToDeal' in action.payload &&
        action.payload.AuthorityToDeal?.length > 0
      ) {
        const authorityData = action.payload.AuthorityToDeal[0];
        returnState.additionalPolicyHolder = {
          AddressSameAsMain: authorityData.UsePrimaryContactAddress ?? true,
          DateOfBirth: authorityData.DateOfBirth
            ? new Date(authorityData.DateOfBirth)
            : undefined,
          ...(authorityData.Email && { Email: authorityData.Email }),
          FirstName: authorityData.FirstName,
          LastName: authorityData.LastName,
          TitleId: authorityData.TitleId,
        };
      }

      // fix up home cat
      if (returnState.activeQuote?.SelectedCoverOptionId) {
        if (
          returnState.activeQuote?.Pet?.SubSpeciesId ===
          subSpeciesIds.find((species) => species.name === 'home cat')?.id
        ) {
          returnState.extraData = {
            ...returnState.extraData,
            Homecat: true,
          };
        }
      }

      // Additional logic to ensure Homecat persists for cat quotes
      if (speciesId === 'c2597178-071f-4940-825d-4d7351b5ffa4') {
        returnState.activeQuote = {
          ...returnState.activeQuote,
          Pet: {
            ...returnState.activeQuote?.Pet,
            Homecat:
              returnState.extraData?.Homecat ??
              returnState.activeQuote?.Pet?.Homecat,
          },
        };
      }

      if (action.payload?.BasketId) {
        returnState.basketId = action.payload?.BasketId;
      }

      return returnState;
    }

    case 'SET_ACTIVE_QUOTE_BY_ID': {
      const basketQuote = state.basket?.Quotes.find(
        (q) => q.Id === action.payload,
      );
      // Preserve AllowFollowUp — basket quotes always return the API default (false)
      const activeQuote =
        basketQuote && state.activeQuote?.AllowFollowUp !== undefined
          ? { ...basketQuote, AllowFollowUp: state.activeQuote.AllowFollowUp }
          : basketQuote;
      return {
        ...state,
        activeQuote,
      };
    }
    case 'SET_ACTIVE_PET': {
      const extraData = {
        ...state.extraData,
      };

      if (typeof action.payload?.Homecat === 'boolean') {
        extraData.Homecat = action.payload?.Homecat;
      } else {
        delete extraData.Homecat;
      }

      const updatePet = {
        ...state.activeQuote?.Pet,
        ...action.payload,
      };

      if ('SubSpeciesId' in action.payload) {
        updatePet.SubSpeciesId = action.payload.SubSpeciesId;
      }

      const returnData = {
        ...state,
        extraData,
        activeQuote: {
          ...state.activeQuote,
          Pet: updatePet,
        },
      };

      return returnData;
    }
    case 'SET_ACTIVE_CUSTOMER':
      return {
        ...state,
        activeQuote: {
          ...state.activeQuote,
          Customer: {
            ...state.activeQuote?.Customer,
            ...action.payload,
          },
        },
      };
    case 'SET_ALLOW_FOLLOW_UP':
      return {
        ...state,
        activeQuote: state.activeQuote
          ? { ...state.activeQuote, AllowFollowUp: action.payload }
          : state.activeQuote,
      };
    case 'SET_ADDITIONAL_POLICY_HOLDER': {
      // Normalise: if an array was passed, keep only the first element
      const rawPayload = Array.isArray(action.payload)
        ? (action.payload[0] ?? null)
        : action.payload;

      if (rawPayload !== action.payload) {
        console.warn(
          '⚠️ SET_ADDITIONAL_POLICY_HOLDER: received array payload — only the first element will be used',
        );
      }

      if (rawPayload === null) {
        return { ...state, additionalPolicyHolder: null };
      }

      // Merge incoming partial payload over existing value, then strip undefined keys
      // so previously known fields (Email, DateOfBirth, etc.) aren't wiped when a
      // partial update doesn't include them.
      const merged: Partial<AuthorityToDeal> = Object.fromEntries(
        Object.entries({
          ...state.additionalPolicyHolder,
          ...rawPayload,
        }).filter(([, v]) => v !== undefined),
      ) as Partial<AuthorityToDeal>;

      // Deduplication: only update if the merged result actually differs
      if (
        areAdditionalPolicyHoldersEqual(state.additionalPolicyHolder, merged)
      ) {
        console.debug('✓ Additional policy holder unchanged, skipping update');
        return state;
      }

      return {
        ...state,
        additionalPolicyHolder: merged,
      };
    }
    case 'REMOVE_ADDITIONAL_POLICY_HOLDER':
      return {
        ...state,
        additionalPolicyHolder: null,
      };

    case 'SET_BASKET': {
      if (!action.payload) {
        return state;
      }

      // The basket is the ultimate source of truth for order.
      const chronologicalQuoteIdsFromBasket = action.payload.Quotes.map(
        (q) => q.Id,
      );

      // Preserve UserSetExcessId for all quotes in the basket
      // Also preserve AllowFollowUp
      const updatedQuotes = action.payload.Quotes.map((newQuote) => ({
        ...newQuote,
        ...(state.activeQuote?.AllowFollowUp !== undefined && {
          AllowFollowUp: state.activeQuote.AllowFollowUp,
        }),
      }));

      const prevQuote = updatedQuotes.find(
        (quote) => quote.Id === state.activeQuote?.Id,
      );

      let currentQuote = prevQuote ?? { ...state.activeQuote };

      try {
        Sentry.addBreadcrumb({
          category: 'basket',
          message: prevQuote
            ? `Found matching quote in basket`
            : `No matching quote found in basket`,
          level: 'info',
        });
      } finally {
        // NOOP
      }

      if (!state.activeQuote?.Id) {
        try {
          Sentry.addBreadcrumb({
            category: 'basket',
            message: `Setting current quote to first quote in basket`,
            level: 'info',
          });
        } finally {
          // NOOP
        }
        currentQuote = action.payload?.Quotes?.[0];
      }

      if (currentQuote) {
        // Preserve excess fields from activeQuote to prevent stale API data overwriting user selections
        currentQuote = {
          ...currentQuote,
          UserSetExcessId:
            state.activeQuote?.UserSetExcessId ?? currentQuote.UserSetExcessId,
          SelectedFixedExcessId:
            state.activeQuote?.SelectedFixedExcessId ??
            currentQuote.SelectedFixedExcessId,
          SelectedVariableExcessId:
            state.activeQuote?.SelectedVariableExcessId ??
            currentQuote.SelectedVariableExcessId,
          PaymentSchedule:
            state.activeQuote?.PaymentSchedule ?? currentQuote.PaymentSchedule,
          MonthlyRate:
            state.activeQuote?.MonthlyRate ?? currentQuote.MonthlyRate,
          DiscountedRate:
            state.activeQuote?.DiscountedRate ?? currentQuote.DiscountedRate,
          ...(state.activeQuote?.AllowFollowUp !== undefined && {
            AllowFollowUp: state.activeQuote.AllowFollowUp,
          }),
        };

        // Handle AuthorityToDeal if it exists
        if (currentQuote.AuthorityToDeal?.length > 0) {
          const [firstAuthority] = currentQuote.AuthorityToDeal;
          state.additionalPolicyHolder = firstAuthority;
        }
      } else {
        // If no matching quote found, use the existing activeQuote

        currentQuote = { ...state.activeQuote };
        try {
          Sentry.addBreadcrumb({
            category: 'basket',
            message: `No matching quote found, use the existing activeQuote`,
            level: 'info',
          });
        } finally {
          // NOOP
        }
      }

      // loop basket quotes.
      // for each put the id into the correct species
      // if no species then add it

      const requestedQuotes = updatedQuotes.reduce<RequestedQuotes>(
        (prev, curr) => {
          const speciesId = curr.Pet.SpeciesId;
          if (speciesId) {
            if (!prev[speciesId]) {
              prev[speciesId] = { quoteIds: [], count: 0 };
            }
            if (!prev[speciesId]?.quoteIds?.includes(curr.Id)) {
              prev[speciesId]?.quoteIds?.push(curr.Id);
              // Only increment count for new quotes
              prev[speciesId].count = prev[speciesId]?.quoteIds?.length || 0;
            }
          }
          return prev;
        },
        { ...state.requestedQuotes },
      );

      try {
        Sentry.setContext('basket', {
          Id: action.payload?.Id,
          reference: action.payload?.Reference,
          promoCode: action.payload?.PromoCode,
          existingUserWithEmail: action.payload?.ExistingUserWithEmail,
          policieCount: action.payload?.Policies?.length || 0,
          quoteCount: updatedQuotes.length,
        });

        if (action.payload?.Id) {
          Sentry.setTag('basket_id', action.payload.Id);
        }
      } catch (error) {
        // do nothing
      }

      return {
        ...state,
        basket: {
          ...action.payload,
          Quotes: updatedQuotes,
        },
        basketId: action.payload.Id,
        activeQuote: currentQuote,
        requestedQuotes,
        // Preserve userSetExcessIds
        userSetExcessIds: state.userSetExcessIds || {},
      };
    }

    case 'SET_EDIT_MODE': {
      return {
        ...state,
        editMode: action.payload ?? null,
      };
    }

    case 'SET_PET_INFO':
      return {
        ...state,
        petInfos: {
          ...state.petInfos,
          [action.payload.key]: action.payload.info,
        },
      };

    case 'SET_EXCESS': {
      const {
        selectedExcessId,
        value,
        selectedFixedExcessId,
        fixedExcessValue,
        selectedVariableExcessId,
        variableExcessValue,
        quoteId,
      } = action.payload;

      // Build the update object based on which values are provided
      const updates: any = {};

      // Handle legacy single excess format
      if (selectedExcessId && value) {
        updates[selectedExcessId] = value;
      }

      // Handle new fixed excess format
      if (selectedFixedExcessId && fixedExcessValue) {
        updates.SelectedFixedExcessId = fixedExcessValue;
      }

      // Handle new variable excess format
      if (selectedVariableExcessId && variableExcessValue) {
        updates.SelectedVariableExcessId = variableExcessValue;
      }

      const updatedActiveQuote = {
        ...state.activeQuote,
        ...updates,
      };

      const updatedBasketQuotes =
        state.basket?.Quotes.map((quote) =>
          quote.Id === quoteId ? { ...quote, ...updates } : quote,
        ) ?? [];

      return {
        ...state,
        activeQuote: updatedActiveQuote,
        basket: state.basket
          ? {
              ...state.basket,
              Quotes: updatedBasketQuotes,
            }
          : null,
        userSetExcessIds: {
          ...state.userSetExcessIds,
          [quoteId]: true,
        },
      };
    }

    case 'REMOVE_PET_INFOS_WITHOUT_POLICY_VALUE': {
      const filteredPetInfos = Object.entries(state.petInfos).reduce(
        (acc, [key, info]) => {
          if (info.policy_value != null) {
            // Checks for both undefined and null
            acc[key] = info;
          }
          return acc;
        },
        {} as Record<string, any>,
      );

      return {
        ...state,
        petInfos: filteredPetInfos,
      };
    }

    case 'REMOVE_EXCESS': {
      if (!state.activeQuote) return state;

      const updatedActiveQuote = {
        ...state.activeQuote,
        SelectedFixedExcessId: undefined,
        SelectedVariableExcessId: undefined,
      };

      const updatedBasketQuotes =
        state.basket?.Quotes.map((quote) =>
          quote.Id === state.activeQuote?.Id
            ? {
                ...quote,
                SelectedFixedExcessId: undefined,
                SelectedVariableExcessId: undefined,
              }
            : quote,
        ) ?? [];

      const updatedUserSetExcessIds = { ...state.userSetExcessIds };
      delete updatedUserSetExcessIds[state.activeQuote.Id];

      return {
        ...state,
        activeQuote: updatedActiveQuote,
        basket: state.basket
          ? {
              ...state.basket,
              Quotes: updatedBasketQuotes,
            }
          : null,
        userSetExcessIds: updatedUserSetExcessIds,
      };
    }

    case 'SET_DIRECT_DEBIT_AMOUNT': {
      const existingAmounts = state.directDebitAmounts || [];
      const existingIndex = existingAmounts.findIndex(
        (item) => item.policyId === action.payload.policyId,
      );

      if (existingIndex >= 0) {
        const updatedAmounts = [...existingAmounts];
        updatedAmounts[existingIndex] = action.payload;
        return {
          ...state,
          directDebitAmounts: updatedAmounts,
        };
      }

      return {
        ...state,
        directDebitAmounts: [...existingAmounts, action.payload],
      };
    }

    case 'SET_QUOTE_TYPE':
      return {
        ...state,
        quoteType: action.payload,
      };

    default:
      return state;
  }
};

export const QuoteContext = createContext<QuoteContextStateReturn>(
  initialState as QuoteContextStateReturn,
);

export const QuoteContextProvider = ({
  children,
  location,
}: QuoteContextProviderProps) => {
  const qs = getQueryString<QueryContextQueryStrings>(location?.search);
  const brand = useBrand();
  const [state, dispatch] = useReducer<
    React.Reducer<QuoteContextState, Action>
  >(reducer, initialState);
  const { subSpecies } = useSiteMetadata();

  const getDataLayerContentType = useGetDataLayerContentType();

  const { campaignId, partnerTrackingId } = useCampaignId();

  // Hook used too sync context to session storage, to allow refreshes of the page etc
  useSyncContextToSessionStorage('QuoteContext', { state, dispatch });

  // Check for default /override promo code
  const defaultPromoCode = useDefaultPromoCode();

  // Hook to get Basket data
  const {
    data: basketData,
    isLoading: basketLoading,
    fetchBasket: fetchBasketFnc,
    refetch: refetchBasket,
    error: basketError,
  } = useGetBasket();

  const { mutateAsync: getAffiliate } = useAffiliateLookup();

  const fetchBasket = useCallback(() => {
    const id = state.basketId || state.activeQuote?.BasketId;
    console.debug('fetchBasket', id);

    if (id) {
      console.debug('fetching basket', id);
      fetchBasketFnc(id);
    } else {
      console.debug('no basket id');
    }
  }, [fetchBasketFnc, state.activeQuote?.BasketId, state.basketId]);

  // Logic to get and save Brand
  useEffect(() => {
    if (brand && !state.brand?.brandId) {
      dispatch({
        type: 'SET_BRAND',
        payload: brand,
      });
    }
  }, [brand, state.brand?.brandId]);

  // Logic to set Promo Code
  useEffect(() => {
    if (qs?.promocode || qs?.pc) {
      dispatch({
        type: 'SET_PROMO_CODE',
        payload: qs.promocode || qs.pc || '',
      });
    }
  }, [qs?.promocode, qs?.pc]);

  // Logic to set Partner Tracking ID
  useEffect(() => {
    if (qs?.partnertrackingid || qs?.partnerTrackingDd) {
      dispatch({
        type: 'SET_PARTNER_ID',
        payload: qs.partnertrackingid || qs.partnerTrackingDd || '',
      });
    }
  }, [qs?.partnerTrackingDd, qs?.partnertrackingid]);

  useEffect(() => {
    const currentCamId =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('agria_campaignid')
        : null;
    if (qs?.camid) {
      dispatch({
        type: 'SET_CAMPAIGN_ID',
        payload: qs.camid,
      });
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('agria_campaignid', qs.camid);
      }
    }
    if (campaignId && !currentCamId && !qs?.camid) {
      dispatch({
        type: 'SET_CAMPAIGN_ID',
        payload: campaignId,
      });
    }
  }, [campaignId, qs?.camid]);

  useEffect(() => {
    if (partnerTrackingId) {
      dispatch({
        type: 'SET_PARTNER_ID',
        payload: partnerTrackingId,
      });
    }
  }, [partnerTrackingId]);

  useEffect(() => {
    if (qs?.basketId) {
      dispatch({
        type: 'SET_BASKET_ID',
        payload: qs.basketId,
      });
    }
  }, [qs?.basketId]);

  // Logic to get and save Affiliate ID
  useEffect(() => {
    if (qs?.affid) {
      // Parse the incoming affid (we support a GUID or an Agria ID).
      const parsedAffiliateId = qs.affid.replace(/[^a-zA-Z0-9-]/g, '');
      if (isGUID(parsedAffiliateId)) {
        dispatch({
          type: 'SET_AFFILIATE_ID',
          payload: parsedAffiliateId,
        });
      } else {
        getAffiliate({ agriaId: parsedAffiliateId })
          .then((response) => {
            dispatch({
              type: 'SET_AFFILIATE_ID',
              payload: response.Id,
            });
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }
  }, [getAffiliate, qs?.affid]);

  // Logic to get and save SS KEY - for Optimise

  useEffect(() => {
    if (qs?.sskey) {
      // Future self this is also in packages/themes/brochureware/gatsby-browser.tsx
      setCookie('sskey', qs.sskey, 30);
      dispatch({
        type: 'SET_SSKEY',
        payload: qs.sskey,
      });
    } else if (getCookie('sskey')) {
      dispatch({
        type: 'SET_SSKEY',
        payload: getCookie('sskey'),
      });
    }
  }, [qs?.sskey]);

  // Logic to get and save UTM source - for Optimise

  useEffect(() => {
    if (qs?.utm_source) {
      dispatch({
        type: 'SET_UTM_SOURCE',
        payload: qs.utm_source,
      });
    }
  }, [qs?.utm_source]);

  // Logic to get Basket via Guid
  useEffect(() => {
    if (qs?.quoterefGuid) {
      fetchBasketFnc(qs?.quoterefGuid);
    }
  }, [fetchBasketFnc, qs?.quoterefGuid]);

  // logic to pickup grabing the basket and set it to state
  useEffect(() => {
    if (basketData) {
      dispatch({
        type: 'SET_BASKET',
        payload: basketData,
      });
      try {
        Sentry.setContext('basket', {
          id: basketData?.Id,
          numberOfPolicies: basketData?.Policies?.length,
          numberOfQuotes: basketData?.Quotes?.length,
        });
      } catch (error) {
        // do nothing
      }
    }
  }, [basketData]);

  // Check for a stale basket
  // if there is a basket in the store but no basket data from the api
  // useEffect(() => {
  //   if (!basketLoading && !basketData && state.basket && state.basketId) {
  //     fetchBasket();
  //   }
  // }, [basketData, basketLoading, fetchBasket, state.basket, state.basketId]);

  // how may quotes have been requested
  const totalQuoteRequests: number = state?.requestedQuotes
    ? Object.entries(state.requestedQuotes).reduce(
        (prev, [_, quoteDetails]) => prev + quoteDetails.count ?? 0,
        0,
      )
    : 0;

  // how many quotes have been completed)
  const totalCompletedQuoteRequests = state?.requestedQuotes
    ? Object.entries(state.requestedQuotes).reduce(
        (prev, [_, quoteDetails]) =>
          prev + (quoteDetails?.quoteIds?.length ?? 0),
        0,
      )
    : 0;

  // Set our dataLayerContentType and make available in state
  useEffect(() => {
    const datalayerContentType = getDataLayerContentType(
      state.requestedQuotes as RequestedQuotes,
      subSpecies,
    );

    dispatch({
      type: 'SET_DATALAYER_CONTENT_TYPE',
      payload: datalayerContentType,
    });
  }, [getDataLayerContentType, state.requestedQuotes]);

  // Set default promo code if not already set and it exists
  useEffect(() => {
    if (!state.promoCode && defaultPromoCode) {
      dispatch({
        type: 'SET_PROMO_CODE',
        payload: defaultPromoCode,
      });
    }
  }, [state.promoCode, defaultPromoCode]);

  const returnValue: QuoteContextStateReturn = useMemo(() => {
    // activeSpecies ID
    const activeSpecies: string | null =
      state?.activeQuote?.Pet?.SpeciesId || null;

    // activeSpecies quoteIds
    const activeSpeciesQuoteIds =
      activeSpecies && state.requestedQuotes
        ? state.requestedQuotes?.[activeSpecies]?.quoteIds
        : [];

    // Get current species count. e.g if Active quote is for a cat, how many cats have been completed so far + 1
    let activeSpeciesCount = 1;
    const activeSpeciesTotalCount =
      activeSpecies && state.requestedQuotes?.[activeSpecies]
        ? state.requestedQuotes[activeSpecies]?.count
        : 1;

    if (
      activeSpecies &&
      activeSpeciesQuoteIds?.length &&
      state.activeQuote?.Id
    ) {
      activeSpeciesCount =
        activeSpeciesQuoteIds.indexOf(state.activeQuote.Id) + 1;
    } else {
      activeSpeciesCount = activeSpeciesQuoteIds?.length
        ? activeSpeciesQuoteIds.length + 1
        : 1;
    }

    // Get total count of active animals across all species
    const allActiveSpeciesTotalCount = state?.requestedQuotes
      ? Object.entries(state.requestedQuotes).reduce(
          (prev, [_, quoteDetails]) => prev + (quoteDetails.count ?? 0),
          0,
        )
      : 0;

    let allActiveSpeciesCount = 0;
    const activeQuoteId = state.activeQuote?.Id;

    if (activeQuoteId) {
      const index = state.chronologicalQuoteIds.indexOf(activeQuoteId);
      allActiveSpeciesCount =
        index !== -1 ? index + 1 : state.chronologicalQuoteIds.length + 1;
    } else {
      allActiveSpeciesCount = state.chronologicalQuoteIds.length + 1;
    }

    // promoCode applied to any Quote in the Basket
    const promoCode =
      state.promoCode ||
      defaultPromoCode ||
      state?.basket?.Quotes?.find((quote) => !!quote.PromoCode)?.PromoCode;

    return {
      ...state,
      activeSpecies,
      activeSpeciesCount,
      activeSpeciesTotalCount,
      allActiveSpeciesTotalCount,
      allActiveSpeciesCount,
      dispatch,
      fetchBasket,
      basketLoading,
      basketError,
      totalQuoteRequests,
      totalCompletedQuoteRequests,
      policyIds: state.policyIds,
      refetchBasket,
      hasBasket: !!basketData,
      promoCode,
      quoteType: state.quoteType,
    };
  }, [
    state,
    fetchBasket,
    basketLoading,
    basketError,
    totalQuoteRequests,
    totalCompletedQuoteRequests,
    refetchBasket,
    basketData,
    defaultPromoCode,
  ]);

  return (
    <QuoteContext.Provider value={returnValue}>
      {children}
    </QuoteContext.Provider>
  );
};

QuoteContext.displayName = 'QuoteContext';
